import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyPhotoWebApiHandler_pollPhotoIds } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { getListingDetailsQueryKey } from './properties';

import type { GetUserPropertyDetailsResponseWebDto, ListingAlias } from '@zg-rentals/ts-rental-manager-api-client';

/** Mirrors the backend rentalManagerApi.photoUpload.uploadUrlValidityDuration setting. */
export const QR_PHOTO_UPLOAD_POLL_WINDOW_MS = 30 * 60 * 1000;
const POLL_INTERVAL_MS = 5000;
/** Retry window for an unchanged-but-still-diverging photo id set, so a failed details refetch recovers. */
const INVALIDATE_COOLDOWN_MS = 30_000;

const toComparablePhotoIds = (photoIds: Array<string>) => [...photoIds].sort().join(',');

export const useQrPhotoUploadPolling = ({
  listingAlias,
  pollUntilMs,
}: {
  listingAlias: string;
  pollUntilMs: number | undefined;
}) => {
  const queryClient = useQueryClient();
  const lastInvalidatedRef = useRef<{ photoIds: string; atMs: number } | null>(null);

  const { data, dataUpdatedAt } = useRentalManagerUserPropertyPhotoWebApiHandler_pollPhotoIds(
    { listingAlias: listingAlias as ListingAlias },
    {
      enabled: Boolean(listingAlias) && pollUntilMs !== undefined && Date.now() < pollUntilMs,
      refetchInterval: () => (pollUntilMs !== undefined && Date.now() < pollUntilMs ? POLL_INTERVAL_MS : false),
      refetchIntervalInBackground: false,
      refetchOnReconnect: false,
      meta: {
        tags: ['listingPhotoIds'],
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyPhotoWebApiHandler_pollPhotoIds',
        },
      },
    },
  );

  useEffect(() => {
    lastInvalidatedRef.current = null;
  }, [listingAlias]);

  useEffect(() => {
    if (data == null) {
      return;
    }
    const detailsQueryKey = getListingDetailsQueryKey(listingAlias);
    const details = queryClient.getQueryData<GetUserPropertyDetailsResponseWebDto>(detailsQueryKey);
    if (details == null) {
      return;
    }
    const cachedPhotoIds = details.propertyPhotos.map((photo) => photo.id);
    const polledPhotoIds = toComparablePhotoIds(data.photoIds);
    if (toComparablePhotoIds(cachedPhotoIds) === polledPhotoIds) {
      return;
    }
    const lastInvalidated = lastInvalidatedRef.current;
    const isWithinCooldown =
      lastInvalidated?.photoIds === polledPhotoIds && Date.now() - lastInvalidated.atMs < INVALIDATE_COOLDOWN_MS;
    if (isWithinCooldown) {
      return;
    }
    lastInvalidatedRef.current = { photoIds: polledPhotoIds, atMs: Date.now() };
    queryClient.invalidateQueries({ queryKey: detailsQueryKey });
    // dataUpdatedAt: react-query structurally shares an unchanged photoIds array, so run this on every poll.
  }, [data, dataUpdatedAt, listingAlias, queryClient]);
};

/** Polls for photos uploaded from a phone for as long as the generated link stays valid. */
export const useQrPhotoUploadPollSession = (listingAlias: string) => {
  const [pollSession, setPollSession] = useState<{ listingAlias: string; pollUntilMs: number } | undefined>();

  const startQrUploadPollWindow = useCallback(
    () => setPollSession({ listingAlias, pollUntilMs: Date.now() + QR_PHOTO_UPLOAD_POLL_WINDOW_MS }),
    [listingAlias],
  );

  // Scoped during render so a session started on a previously viewed listing never polls the current one.
  const pollUntilMs = pollSession?.listingAlias === listingAlias ? pollSession.pollUntilMs : undefined;

  useEffect(() => {
    if (pollUntilMs === undefined) {
      return;
    }
    const expiryTimeout = setTimeout(() => setPollSession(undefined), Math.max(0, pollUntilMs - Date.now()));
    return () => clearTimeout(expiryTimeout);
  }, [pollUntilMs]);

  useQrPhotoUploadPolling({ listingAlias, pollUntilMs });

  return { startQrUploadPollWindow };
};
